import { useCallback, useEffect, useRef } from 'react';
import isEqual from 'lodash/isEqual';

// Serialize saves so an older request cannot finish after a newer one.
export default function useWorkoutAutosave({
  workout,
  setWorkout,
  persist,
  onError,
  onSaved,
}) {
  const latestWorkout = useRef(workout);
  latestWorkout.current = workout;
  const callbacks = useRef({ persist, onError, onSaved });
  callbacks.current = { persist, onError, onSaved };
  const version = useRef(0);
  const timer = useRef(null);
  const dirty = useRef(null);
  const pending = useRef([]);
  const saving = useRef(false);
  const active = useRef(null);
  const savedVersions = useRef(new Map());
  const mounted = useRef(true);

  const drain = useCallback(async () => {
    if (saving.current) return;
    saving.current = true;
    while (pending.current.length) {
      const request = pending.current.shift();
      active.current = request;
      if (
        dirty.current?.workout._id === request.workout._id &&
        dirty.current.version === request.version
      ) {
        clearTimeout(timer.current);
        timer.current = null;
        dirty.current = null;
      }
      try {
        const savedWorkout = await callbacks.current.persist(request.workout);
        savedVersions.current.set(request.workout._id, request.version);
        if (
          mounted.current &&
          latestWorkout.current?._id === request.workout._id &&
          version.current === request.version
        ) {
          latestWorkout.current = savedWorkout;
          setWorkout(savedWorkout);
          callbacks.current.onSaved?.();
        }
      } catch (error) {
        if (mounted.current) callbacks.current.onError?.(error);
      }
      active.current = null;
    }
    saving.current = false;
  }, [setWorkout]);

  const queue = useCallback(
    (request) => {
      if (
        savedVersions.current.get(request.workout._id) === request.version ||
        (active.current?.workout._id === request.workout._id &&
          active.current.version === request.version)
      )
        return;
      const index = pending.current.findIndex(
        (entry) => entry.workout._id === request.workout._id
      );
      if (index < 0) pending.current.push(request);
      else pending.current[index] = request;
      return drain();
    },
    [drain]
  );

  const changeWorkout = useCallback(
    (next) => {
      const previous = latestWorkout.current;
      const value = typeof next === 'function' ? next(previous) : next;
      if (isEqual(previous, value)) return;
      if (dirty.current && dirty.current.workout._id !== value?._id)
        queue(dirty.current);
      latestWorkout.current = value;
      version.current += 1;
      setWorkout(value);
      const request = { workout: value, version: version.current };
      const pendingIndex = pending.current.findIndex(
        (entry) => entry.workout._id === value?._id
      );
      if (pendingIndex >= 0) pending.current[pendingIndex] = request;
      if (
        !isEqual(previous?.exerciseGroups, value?.exerciseGroups) ||
        !isEqual(previous?.exercises, value?.exercises)
      ) {
        dirty.current = request;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          timer.current = null;
          const changes = dirty.current;
          dirty.current = null;
          if (changes?.workout?._id) queue(changes);
        }, 500);
      } else if (dirty.current) {
        // Include other edits made while an exercise save is scheduled.
        dirty.current = request;
      }
    },
    [queue, setWorkout]
  );

  const saveWorkout = useCallback(
    (value = latestWorkout.current) => {
      clearTimeout(timer.current);
      timer.current = null;
      dirty.current = null;
      if (value?._id)
        return queue({ workout: value, version: version.current });
    },
    [queue]
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(timer.current);
      if (dirty.current?.workout?._id) queue(dirty.current);
      dirty.current = null;
    };
  }, [queue]);

  return { changeWorkout, saveWorkout };
}
